import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core'
import { ToastrService } from 'ngx-toastr'

/**
 * Reusable "Sharing options" pop-up for a link: QR code (download PNG or SVG), copy, WhatsApp,
 * email, LinkedIn, Facebook, X and the device share sheet. Same look as the parking page share modal.
 *
 * <app-share-link-modal *ngIf="open" [url]="link" title="Share" (closed)="open = false"></app-share-link-modal>
 */
@Component({
    selector: 'app-share-link-modal',
    templateUrl: './share-link-modal.component.html',
    styleUrls: ['./share-link-modal.component.scss'],
})
export class ShareLinkModalComponent {
    @Input() url = ''
    @Input() title = 'Sharing options'
    /** Short line shown above the link */
    @Input() description = ''
    /** Message placed before the link in WhatsApp / X / native share */
    @Input() shareText = ''
    @Input() emailSubject = 'Check this out on ParkUpp'
    /** File name for QR downloads, without extension */
    @Input() fileName = 'parkupp-qr-code'
    @Output() closed = new EventEmitter<void>()

    copied = false
    readonly canNativeShare = typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function'

    constructor(private host: ElementRef<HTMLElement>, private toastr: ToastrService) {}

    private get message(): string {
        return this.shareText ? `${this.shareText} ${this.url}` : this.url
    }

    close() {
        this.closed.emit()
    }

    async copy() {
        try {
            await navigator.clipboard.writeText(this.url)
        } catch {
            const area = document.createElement('textarea')
            area.value = this.url
            area.style.position = 'fixed'
            area.style.left = '-9999px'
            document.body.appendChild(area)
            area.select()
            const ok = document.execCommand('copy')
            document.body.removeChild(area)
            if (!ok) {
                this.toastr.error('Could not copy. Select the link and copy it manually.')
                return
            }
        }
        this.copied = true
        this.toastr.success('Link copied')
        setTimeout(() => (this.copied = false), 2000)
    }

    downloadPng() {
        // Hidden high-resolution canvas so the PNG prints cleanly
        const canvas = this.host.nativeElement.querySelector('.qr-print canvas') as HTMLCanvasElement | null
        if (!canvas) return
        this.save(canvas.toDataURL('image/png'), `${this.fileName}.png`)
    }

    downloadSvg() {
        const svg = this.host.nativeElement.querySelector('.qr-display svg') as SVGElement | null
        if (!svg) return
        const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' })
        const href = URL.createObjectURL(blob)
        this.save(href, `${this.fileName}.svg`)
        setTimeout(() => URL.revokeObjectURL(href), 1000)
    }

    private save(href: string, name: string) {
        const link = document.createElement('a')
        link.href = href
        link.download = name
        link.click()
    }

    whatsapp() {
        window.open(`https://wa.me/?text=${encodeURIComponent(this.message)}`, '_blank', 'noopener')
    }

    email() {
        window.location.href = `mailto:?subject=${encodeURIComponent(this.emailSubject)}&body=${encodeURIComponent(this.message)}`
    }

    linkedIn() {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.url)}`, '_blank', 'noopener')
    }

    facebook() {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.url)}`, '_blank', 'noopener')
    }

    x() {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(this.shareText)}&url=${encodeURIComponent(this.url)}`, '_blank', 'noopener')
    }

    async nativeShare() {
        try {
            await (navigator as any).share({ title: this.emailSubject, text: this.shareText, url: this.url })
        } catch {
            // Cancelled by the user
        }
    }
}
